import clsx from "classnames";
import { Container } from "react-bootstrap";
import { Description } from "@/ui";
import TableIntro from "./components/TableIntro";
import TableTitleBar from "./components/TableTitleBar";
import TableHead from "./components/TableHead";
import TableRow from "./components/TableRow";
import TableCta from "./components/TableCta";
import classes from "./styles.module.scss";

/**
 * Branded content table: an optional heading and intro above everything, a coloured title bar with
 * tag pills and a badge, the table card itself, and an optional call-to-action strip underneath.
 * Columns and rows are authored as separate lists so editors can add, delete or reorder either.
 *
 * @param {string} [className] - Additional classes for the root element (spacing).
 * @param {string} [bgColor] - Background colour class for the section.
 * @param {string} [title] - Heading shown above the title bar.
 * @param {string} [titleColor] - Text colour class for the heading.
 * @param {string} [description] - Intro rich text under the heading.
 * @param {string} [textColor] - Text colour class for the intro copy.
 * @param {string} [introAlignment] - Text alignment class for the heading and intro copy.
 * @param {boolean} [showTitleBar] - Whether the coloured title bar is rendered.
 * @param {Array} [tags] - Tag pills shown above the title bar heading; each item holds a label.
 * @param {string} [titleBarTitle] - Heading inside the title bar.
 * @param {string} [titleBarText] - Rich text under the title bar heading.
 * @param {string} [badge] - Small pill on the right of the title bar.
 * @param {string} [titleBarColor] - Fill key for the title bar.
 * @param {Array} [columns] - Column definitions; each item holds heading, width and alignment.
 * @param {Array} [rows] - Table rows; each item holds cell1 to cell6, read in column order.
 * @param {string} [headerRowColor] - Fill key for the header row.
 * @param {boolean} [showStripes] - Whether alternating rows are tinted.
 * @param {boolean} [showRowDividers] - Whether a divider line is drawn between rows.
 * @param {string} [firstColumnStyle] - "pill", "bold" or "plain" emphasis for the first column.
 * @param {string} [tableMinWidth] - Minimum table width before the card scrolls sideways.
 * @param {boolean} [showCta] - Whether the call-to-action strip is rendered.
 * @param {string} [ctaText] - Rich text in the call-to-action strip.
 * @param {string} [ctaLabel] - Button label in the call-to-action strip.
 * @param {string} [ctaHref] - Button link in the call-to-action strip.
 * @param {boolean} [ctaNewTab] - Open the button link in a new tab.
 * @param {string} [ctaColor] - Fill key for the call-to-action strip.
 * @param {string} [footnote] - Small print under the table.
 * @returns {JSX.Element}
 */
export const Table = ({
  className = "py-5",
  bgColor = "bg-white",
  title,
  titleColor = "text-primary",
  description,
  textColor = "text-body",
  introAlignment = "text-start",
  showTitleBar = true,
  tags = [],
  titleBarTitle,
  titleBarText,
  badge,
  titleBarColor = "gradient",
  columns = [],
  rows = [],
  headerRowColor = "navy",
  showStripes = true,
  showRowDividers = true,
  firstColumnStyle = "pill",
  tableMinWidth = "0",
  showCta = false,
  ctaText,
  ctaLabel,
  ctaHref,
  ctaNewTab = false,
  ctaColor = "orange",
  footnote,
}) => {
  return (
    <div data-component="Table" className={clsx(className, bgColor, classes.table)}>
      <Container>
        <TableIntro
          title={title}
          titleColor={titleColor}
          description={description}
          textColor={textColor}
          alignment={introAlignment}
        />

        <TableTitleBar
          show={showTitleBar}
          tags={tags}
          title={titleBarTitle}
          text={titleBarText}
          badge={badge}
          color={titleBarColor}
        />

        <div className={classes.table__card}>
          <div className={classes.table__scroll} role="region" aria-label={titleBarTitle || title || "Table"} tabIndex={0}>
            <table className={classes.table__table} style={{ minWidth: tableMinWidth !== "0" ? `${tableMinWidth}px` : undefined }}>
              <TableHead columns={columns} color={headerRowColor} />
              <tbody data-sourceflow-prop="rows">
                {rows?.map((row, index) => (
                  <TableRow
                    key={row?.id ?? index}
                    row={row}
                    columns={columns}
                    index={index}
                    showStripes={showStripes}
                    showRowDividers={showRowDividers}
                    firstColumnStyle={firstColumnStyle}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <TableCta show={showCta} text={ctaText} label={ctaLabel} href={ctaHref} newTab={ctaNewTab} color={ctaColor} />

        {!!footnote && (
          <div data-sourceflow-prop="footnote" className={classes.table__footnote}>
            <Description description={footnote} className="mb-0" />
          </div>
        )}
      </Container>
    </div>
  );
};

export default Table;
